import { type ComponentProps, useLayoutEffect, useRef } from "react";

import { Input } from "@/components/ui/field";
import { upperName } from "@/utils/names";

/**
 * What a name field asks of the keyboard.
 *
 * The value is uppercased on the way in, so the phone should not also be
 * offering to capitalise the first letter and autocorrect a surname it has
 * never seen into a word it has.
 */
export const NAME_INPUT = {
  autoCapitalize: "characters",
  autoCorrect: "off",
  spellCheck: false,
} as const;

/**
 * A name field: capitals as they are typed, and the caret left alone.
 *
 * Uppercasing in a plain `onChange` writes a value back that differs from what
 * the box holds, so React assigns `node.value` — and assigning it parks the
 * caret at the end. Typing the U into "JAN" then puts the next keystroke after
 * the N. Legacy works around this by only uppercasing on blur, which leaves the
 * field lying about what it will store for as long as somebody is typing in it.
 *
 * So the offset is taken before the change and put back after the commit.
 * `upperName` is case-only and therefore length-preserving, which is what makes
 * the old offset still the right one; a transform that added or dropped
 * characters would need to count them.
 *
 * Shared, because a member's name is typed in two places: the enrolment form
 * and the Edit Basic Information dialog. One of them capitalising and the other
 * not is how "Dela Cruz" gets back in beside "DELA CRUZ".
 */
export function NameInput({
  value,
  onChange,
  ...props
}: Omit<ComponentProps<typeof Input>, "onChange" | "value"> & {
  value: string;
  onChange: (next: string) => void;
}) {
  const box = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (caret.current === null) return;
    box.current?.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });
  return (
    <Input
      ref={box}
      value={value}
      onChange={(event) => {
        caret.current = event.target.selectionStart;
        onChange(upperName(event.target.value));
      }}
      {...NAME_INPUT}
      {...props}
    />
  );
}
